import { useState, useEffect } from 'react';
import { complianceApi, type TDSEntryRecord } from '@/api/compliance.api';
import { formatINR } from '@/utils/format';

interface Form16ACertificateProps {
  entry: TDSEntryRecord;
  onClose: () => void;
}

interface CompanyProfile {
  name: string;
  address: string;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d as string; }
}

// Income Tax assessment year runs April-March, one year ahead of the financial year of payment.
function assessmentYearFor(paymentDate: string | null | undefined): string {
  const d = paymentDate ? new Date(paymentDate) : new Date();
  const fyStart = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${fyStart + 1}-${String((fyStart + 2) % 100).padStart(2, '0')}`;
}

export default function Form16ACertificate({ entry, onClose }: Form16ACertificateProps) {
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [tan, setTan] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const [companyRes, settingsRes] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/v1/company/get`, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          }).then((r) => r.json()).catch(() => null),
          complianceApi.getTdsTcsSettings().catch(() => null),
        ]);
        if (companyRes?.data) setCompany(companyRes.data);
        if (settingsRes?.success && settingsRes.data?.tan) setTan(settingsRes.data.tan);
      } catch (err) {
        console.error('Failed to load company/TAN for Form 16A:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const certNo = `16A-${assessmentYearFor(entry.paymentDate).split('-')[0]}-${entry.id.split('-').pop()?.slice(0, 6).toUpperCase() || '000001'}`;
  const assessmentYear = assessmentYearFor(entry.paymentDate);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg px-6 py-4 flex items-center gap-2 text-sm text-slate-600">
          <i className="ri-loader-4-line animate-spin text-lg text-indigo-600" /> Loading certificate...
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div
        className="bg-white w-[210mm] min-h-[297mm] shadow-2xl rounded-lg overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 bg-[#1e293b] text-white">
          <div className="flex items-center gap-2">
            <i className="ri-file-shield-line text-emerald-300" />
            <span className="text-sm font-medium">Form 16A Certificate — {entry.partyName}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 h-7 px-3 rounded text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-printer-line" />Print Form 16A
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded hover:bg-white/10 cursor-pointer text-slate-300">
              <i className="ri-close-line" />
            </button>
          </div>
        </div>

        {/* Certificate Content */}
        <div className="p-10 text-[11px] font-sans">
          {/* Header */}
          <div className="text-center mb-8 pb-6 border-b-4 border-double border-[#1e293b]">
            <h1 className="text-xl font-extrabold text-[#1e293b] tracking-wider uppercase">Form 16A</h1>
            <p className="text-sm font-semibold text-[#4f46e5] mt-1">Certificate under Section 203 of the Income Tax Act, 1961</p>
            <p className="text-sm text-[#64748b] mt-1">For Tax Deducted at Source (TDS)</p>
          </div>

          {/* Certificate Info */}
          <div className="flex justify-between mb-6">
            <div>
              <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide">Certificate No</p>
              <p className="font-bold text-base text-[#1e293b]">{certNo}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide">Assessment Year</p>
              <p className="font-bold text-base text-[#1e293b]">{assessmentYear}</p>
            </div>
          </div>

          {/* Deductor (Us) */}
          <div className="mb-5 p-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg">
            <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide font-semibold mb-2">Deductor</p>
            <p className="font-bold text-sm text-[#1e293b]">{company?.name || '—'}</p>
            <p className="text-[#64748b] mt-0.5">TAN: {tan || 'Not configured — set it in TDS/TCS Settings'}</p>
            <p className="text-[#64748b]">{company?.address || '—'}</p>
          </div>

          {/* Deductee (Vendor) */}
          <div className="mb-5 p-4 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg">
            <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide font-semibold mb-2">Deductee</p>
            <p className="font-bold text-sm text-[#1e293b]">{entry.partyName}</p>
            <p className="text-[#64748b] mt-0.5">PAN: {entry.partyPAN}</p>
          </div>

          {/* Details of Payment and Tax Deducted */}
          <div className="mb-5">
            <p className="text-[10px] text-[#94a3b8] uppercase tracking-wide font-semibold mb-3">
              Details of Payment and Tax Deducted
            </p>
            <table className="w-full border-collapse border border-[#e2e8f0] text-[10px]">
              <tbody>
                {[
                  { label: 'Section', value: entry.section },
                  { label: 'Date of Payment', value: formatDate(entry.paymentDate) },
                  { label: 'Amount Paid / Credited', value: formatINR(entry.paymentAmount) },
                  { label: 'TDS Rate', value: `${entry.tdsRate}%` },
                  { label: 'TDS Deducted', value: formatINR(entry.tdsAmount) },
                  { label: 'Date of Deposit', value: formatDate(entry.depositDate) },
                  { label: 'BSR Code', value: entry.bankBSRCode || '—' },
                  { label: 'Challan No', value: entry.tdsChallanNumber || '—' },
                ].map((row, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-[#fafafa]' : ''}>
                    <td className="border border-[#e2e8f0] px-3 py-2 font-medium text-[#64748b] w-48">{row.label}</td>
                    <td className={`border border-[#e2e8f0] px-3 py-2 font-semibold text-[#1e293b] ${row.label === 'TDS Deducted' ? 'text-red-600' : ''
                      }`}>
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Signature */}
          <div className="flex justify-end mt-12 pt-6 border-t border-[#e2e8f0]">
            <div className="text-center text-[10px] text-[#64748b] w-48">
              <p className="font-semibold text-[#1e293b] text-sm">{company?.name || '—'}</p>
              <p className="mt-8 border-t border-[#e2e8f0] pt-1">Authorised Signatory</p>
              <p className="mt-1 italic">(With Seal)</p>
            </div>
          </div>

          <p className="text-center text-[9px] text-[#94a3b8] italic mt-6">
            This certificate is generated based on the provisions of Section 203 of the Income Tax Act, 1961.
            Verify TDS credit in Form 26AS before filing your return.
          </p>
        </div>
      </div>
    </div>
  );
}
